/** @module @liquidefy/code-editor/circuits/theme-applicator @version 1.0.0 @license LQD Research LLC */

/**
 * Theme Applicator Circuit — Tier 1
 *
 * Creates a complete themed environment with brand identity, glass surfaces,
 * ground layers, focus mode, and terminal theme in one call.
 *
 * Composes: themes/identity/brand.ts, themes/identity/presets.ts,
 *           themes/glass/surfaces.ts, themes/glass/backdrop.ts,
 *           themes/composition/layers.ts, themes/adaptive/focus.ts,
 *           terminal/appearance/theme.ts
 *
 * @rust theme_applicator.rs — false; 4/2/26; sm
 */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type { CircuitHandle, CircuitHealthReport, CircuitStats } from './circuit-types.js';
import {
  buildHealthReport,
  emptyStats,
  recordSuccess,
  recordError,
  healthFromStats,
  formatStatsDetails,
} from './circuit-types.js';

import type { ThemeDefinition } from '../code-editor-types.js';

import { createBrand, brandToCSS, brandTypographyToCSS, brandSpacingToCSS } from '../themes/identity/brand.js';
import type { LiquidelyBrand } from '../themes/identity/brand.js';

import { getPresetByName } from '../themes/identity/presets.js';

import {
  createDefaultSurfaces,
  allSurfacesToCSSVariables,
} from '../themes/glass/surfaces.js';
import type { GlassSurfaceSet } from '../themes/glass/surfaces.js';

import { createDefaultDarkGround, createDefaultLightGround, createGroundConfig } from '../themes/glass/backdrop.js';
import type { GroundLayer, GroundMode } from '../themes/glass/backdrop.js';

import {
  enterFocusMode,
  exitFocusMode,
} from '../themes/adaptive/focus.js';
import type { FocusIntensity, FocusThemeState } from '../themes/adaptive/focus.js';

import {
  createTerminalTheme,
  terminalThemeToCSS,
} from '../terminal/appearance/theme.js';
import type { TerminalTheme } from '../terminal/appearance/theme.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 CONFIG & HANDLE INTERFACES
// ═══════════════════════════════════════════════════════════════════════════════

export interface ThemeApplicatorConfig {
  readonly scheme?: 'dark' | 'light';
  readonly preset?: 'dark' | 'light' | 'midnight' | 'high-contrast';
  readonly accentHex?: string;
}

export interface ThemeApplicatorHandle extends CircuitHandle {
  getTheme(): ThemeDefinition;
  getGlassSurfaces(): GlassSurfaceSet;
  getGroundLayer(): GroundLayer;
  getBrand(): LiquidelyBrand;
  switchMode(mode: 'dark' | 'light'): void;
  getCSSVariables(): Record<string, string>;
  getTerminalTheme(): TerminalTheme;
  enterFocusMode(intensity: FocusIntensity): void;
  exitFocusMode(): void;
  stats(): CircuitStats;
  health(): CircuitHealthReport;
  destroy(): void;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 FACTORY
// ═══════════════════════════════════════════════════════════════════════════════

export function createThemeApplicator(config: ThemeApplicatorConfig = {}): ThemeApplicatorHandle {
  // 1. Extract config with defaults
  const presetName = config.preset ?? 'dark';
  const accentHex = config.accentHex ?? '#60a5fa';
  let currentMode: GroundMode = config.scheme ?? (presetName === 'light' ? 'light' : 'dark');

  // 2. Initialize internal state
  let stats: CircuitStats = emptyStats();
  let destroyed = false;
  const createdAt = Date.now();

  // 3. Resolve theme preset
  const presetMap: Record<string, string> = {
    'dark': 'liquidefy dark',
    'light': 'liquidefy light',
    'midnight': 'midnight',
    'high-contrast': 'high contrast',
  };
  let activeTheme: ThemeDefinition | undefined = getPresetByName(presetMap[presetName] ?? presetName);
  if (!activeTheme) {
    // Fallback: use the first available preset
    activeTheme = getPresetByName('liquidefy dark');
  }
  if (!activeTheme) {
    throw new Error(`ThemeApplicator: no theme preset found for '${presetName}'`);
  }

  // 4. Brand
  const brand: LiquidelyBrand = createBrand();

  // 5. Glass surfaces
  let glassSurfaces: GlassSurfaceSet = createDefaultSurfaces();

  // 6. Ground layers
  const darkGround = createDefaultDarkGround();
  const lightGround = createDefaultLightGround();
  let activeGround: GroundLayer = currentMode === 'dark' ? darkGround : lightGround;

  // 7. Terminal theme (mirrors current mode)
  let terminalTheme: TerminalTheme = createTerminalTheme(currentMode, accentHex);

  // 8. Focus mode state
  let focusState: FocusThemeState = {
    isActive: false,
    intensity: 'subtle',
    shift: {
      intensity: 'subtle',
      chromeOpacityMultiplier: 1,
      chromeSaturationMultiplier: 1,
      editorOpacityMultiplier: 1,
      accentDimming: 0,
      warmthShift: 0,
    },
    originalColors: null,
  };

  // 9. Guard
  function guardDestroyed(): void {
    if (destroyed) throw new Error('Circuit destroyed');
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // §4 DOMAIN METHODS
  // ═════════════════════════════════════════════════════════════════════════════

  function doSwitchMode(mode: 'dark' | 'light'): void {
    guardDestroyed();
    currentMode = mode;
    activeGround = mode === 'dark' ? darkGround : lightGround;
    terminalTheme = createTerminalTheme(mode, accentHex);

    // Re-resolve preset if it maps to a mode-specific one
    const modePresetName = mode === 'dark' ? 'liquidefy dark' : 'liquidefy light';
    const modePreset = getPresetByName(modePresetName);
    if (modePreset) activeTheme = modePreset;

    stats = recordSuccess(stats);
  }

  function doGetCSSVariables(): Record<string, string> {
    guardDestroyed();
    // Merge brand CSS + glass CSS + terminal CSS + theme colors
    const brandCSS = brandToCSS(brand);
    const brandTypoCSS = brandTypographyToCSS(brand);
    const brandSpaceCSS = brandSpacingToCSS(brand);
    const glassCSS = allSurfacesToCSSVariables(glassSurfaces);
    const termCSS = terminalThemeToCSS(terminalTheme);

    // Theme definition colors
    const themeColors: Record<string, string> = {};
    if (activeTheme) {
      for (const [key, value] of Object.entries(activeTheme.colors)) {
        themeColors[`--theme-${key}`] = value;
      }
    }

    // Ground layer variables
    const groundCSS: Record<string, string> = {
      '--ground-mode': currentMode,
      '--ground-primary': activeGround.primary,
      '--ground-secondary': activeGround.secondary,
      '--ground-tertiary': activeGround.tertiary,
      '--ground-text-primary': activeGround.textPrimary,
      '--ground-text-secondary': activeGround.textSecondary,
      '--ground-text-tertiary': activeGround.textTertiary,
      '--ground-glass-tint': activeGround.glassTint,
      '--ground-glass-border': activeGround.glassBorder,
    };

    stats = recordSuccess(stats);
    return {
      ...brandCSS,
      ...brandTypoCSS,
      ...brandSpaceCSS,
      ...glassCSS,
      ...termCSS,
      ...themeColors,
      ...groundCSS,
    };
  }

  function doEnterFocusMode(intensity: FocusIntensity): void {
    guardDestroyed();
    if (focusState.isActive) return;
    // Capture colors before focus shift
    const colors = activeTheme ? { ...activeTheme.colors } : {};
    focusState = enterFocusMode(
      focusState,
      colors as Record<string, string>,
      intensity,
    );
    stats = recordSuccess(stats);
  }

  function doExitFocusMode(): void {
    guardDestroyed();
    if (!focusState.isActive) return;
    focusState = exitFocusMode(focusState);
    stats = recordSuccess(stats);
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // §5 FROZEN HANDLE
  // ═════════════════════════════════════════════════════════════════════════════

  return Object.freeze({
    name: 'theme-applicator',

    getTheme(): ThemeDefinition {
      guardDestroyed();
      return activeTheme!;
    },

    getGlassSurfaces(): GlassSurfaceSet {
      guardDestroyed();
      return glassSurfaces;
    },

    getGroundLayer(): GroundLayer {
      guardDestroyed();
      return activeGround;
    },

    getBrand(): LiquidelyBrand {
      guardDestroyed();
      return brand;
    },

    switchMode: doSwitchMode,
    getCSSVariables: doGetCSSVariables,

    getTerminalTheme(): TerminalTheme {
      guardDestroyed();
      return terminalTheme;
    },

    enterFocusMode: doEnterFocusMode,
    exitFocusMode: doExitFocusMode,

    stats(): CircuitStats {
      return { ...stats, uptime: Date.now() - createdAt };
    },

    health(): CircuitHealthReport {
      if (destroyed) return buildHealthReport('theme-applicator', 'destroyed', 'Circuit destroyed', createdAt);
      return buildHealthReport('theme-applicator', healthFromStats(stats), formatStatsDetails(stats), createdAt);
    },

    destroy(): void {
      destroyed = true;
    },
  });
}
